import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import ModuleLayout from '../components/ModuleLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Truck, UserPlus, Users, Package, Clock, Star, Phone, Zap,
  CircleDot, Car, Bike, MoreVertical, RefreshCw, MessageCircle, ChevronDown,
  ExternalLink, Copy, ClipboardCheck, BarChart3, ThumbsUp, MessageSquare, TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  available: { key: 'fleet.statusAvailable', color: 'bg-emerald-500', dot: 'bg-emerald-400' },
  on_delivery: { key: 'fleet.statusOnDelivery', color: 'bg-amber-500', dot: 'bg-amber-400' },
  offline: { key: 'fleet.statusOffline', color: 'bg-zinc-500', dot: 'bg-zinc-400' },
};

const DELIVERY_STATUS = {
  assigned: { key: 'fleet.statusAssigned', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  picked_up: { key: 'fleet.statusPickedUp', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  on_the_way: { key: 'fleet.statusOnTheWay', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  delivered: { key: 'fleet.statusDelivered', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  cancelled: { key: 'fleet.statusCancelled', color: 'text-red-400', bg: 'bg-red-500/10' },
};

const VEHICLE_ICON = { car: Car, motorcycle: Bike, bicycle: Bike };

export default function DeliveryFleetPage() {
  const { token, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();

  const [drivers, setDrivers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('overview');
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: '', phone: '', vehicle_type: 'car' });
  const [readyOrders, setReadyOrders] = useState([]);
  const [assignModal, setAssignModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMsg, setChatMsg] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatSessionId, setChatSessionId] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [copied, setCopied] = useState('');
  const [ratingsData, setRatingsData] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const branchId = activeBranch?.id;

  const fetchData = useCallback(async () => {
    if (!branchId) return;
    try {
      const [driversRes, deliveriesRes, statsRes, ordersRes, ratingsRes] = await Promise.all([
        fetch(`${API}/api/drivers?branch_id=${branchId}`, { headers }),
        fetch(`${API}/api/deliveries?branch_id=${branchId}`, { headers }),
        fetch(`${API}/api/deliveries/stats?branch_id=${branchId}`, { headers }),
        fetch(`${API}/api/orders?branch_id=${branchId}`, { headers }),
        fetch(`${API}/api/ratings/analytics?branch_id=${branchId}`, { headers }),
      ]);
      const [d, dlv, s, o, r] = await Promise.all([driversRes.json(), deliveriesRes.json(), statsRes.json(), ordersRes.json(), ratingsRes.json()]);
      setDrivers(Array.isArray(d) ? d : []);
      setDeliveries(Array.isArray(dlv) ? dlv : []);
      setStats(s);
      setReadyOrders(Array.isArray(o) ? o.filter(order => order.status === 'ready' && !order.driver_id) : []);
      setRatingsData(r);
    } catch (e) { console.error(e); }
  }, [branchId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!branchId) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/fleet/${branchId}`);
    const ping = setInterval(() => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' })); }, 25000);
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (['delivery_updated', 'driver_status_changed', 'new_delivery'].includes(msg.event)) fetchData();
    };
    return () => { clearInterval(ping); ws.close(); };
  }, [branchId, fetchData]);

  const addDriver = async () => {
    if (!newDriver.name || !newDriver.phone) return toast.error(t('fleet.fillAllFields'));
    try {
      const res = await fetch(`${API}/api/drivers`, {
        method: 'POST', headers, body: JSON.stringify({ ...newDriver, branch_id: branchId })
      });
      if (!res.ok) throw new Error();
      toast.success(t('fleet.driverAdded'));
      setNewDriver({ name: '', phone: '', vehicle_type: 'car' });
      setShowAddDriver(false);
      fetchData();
    } catch { toast.error(t('fleet.addFailed')); }
  };

  const deleteDriver = async (id) => {
    try {
      await fetch(`${API}/api/drivers/${id}`, { method: 'DELETE', headers });
      toast.success(t('fleet.deleted'));
      fetchData();
    } catch { toast.error(t('fleet.deleteFailed')); }
  };

  const toggleDriverStatus = async (driver) => {
    const next = driver.status === 'available' ? 'offline' : 'available';
    await fetch(`${API}/api/drivers/${driver.id}/status`, {
      method: 'PUT', headers, body: JSON.stringify({ status: next })
    });
    fetchData();
  };

  const assignOrder = async (orderId, driverId) => {
    try {
      const res = await fetch(`${API}/api/deliveries/assign`, {
        method: 'POST', headers, body: JSON.stringify({ order_id: orderId, driver_id: driverId })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      toast.success(t('fleet.orderAssigned'));
      setAssignModal(null);
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const autoAssign = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/deliveries/auto-assign?branch_id=${branchId}`, { method: 'POST', headers });
      const data = await res.json();
      if (data.success) {
        toast.success(t('fleet.autoAssignSuccess', { count: data.assigned }));
      } else {
        toast.error(data.error || t('common.failed'));
      }
      fetchData();
    } catch { toast.error(t('fleet.autoAssignFailed')); }
    setLoading(false);
  };

  const sendChat = async () => {
    if (!chatMsg.trim()) return;
    const msg = chatMsg;
    setChatMsg('');
    setChatHistory(h => [...h, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const res = await fetch(`${API}/api/agent/delivery/chat?branch_id=${branchId}&message=${encodeURIComponent(msg)}&session_id=${chatSessionId}`, {
        method: 'POST', headers
      });
      const data = await res.json();
      setChatHistory(h => [...h, { role: 'assistant', content: data.text }]);
      if (data.session_id) setChatSessionId(data.session_id);
      if (data.actions_executed?.length) fetchData();
    } catch { setChatHistory(h => [...h, { role: 'assistant', content: t('fleet.chatError') }]); }
    setChatLoading(false);
  };

  const activeDrivers = drivers.filter(d => d.is_active);
  const availableCount = activeDrivers.filter(d => d.status === 'available').length;
  const activeDeliveries = deliveries.filter(d => ['assigned', 'picked_up', 'on_the_way'].includes(d.status));

  if (!branchId) {
    return (
      <ModuleLayout moduleKey="operations">
        <div className="flex items-center justify-center h-[60vh] text-muted-foreground">
          {t('fleet.selectBranchFirst')}
        </div>
      </ModuleLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: t('fleet.overview') },
    { id: 'drivers', label: t('fleet.driversTab') },
    { id: 'deliveries', label: t('fleet.deliveriesTab') },
    { id: 'ratings', label: t('fleet.ratingsTab') },
  ];

  return (
    <ModuleLayout moduleKey="operations">
      <div className="space-y-6" data-testid="delivery-fleet-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="fleet-title">
              <Truck className="w-6 h-6 text-amber-500" />
              {t('fleet.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('fleet.subtitle')}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => window.open(`/driver?branch=${branchId}`, '_blank')} variant="outline" className="gap-2 border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10" data-testid="driver-portal-link">
              <ExternalLink className="w-4 h-4" /> {t('fleet.driverPortal')}
            </Button>
            <Button onClick={autoAssign} disabled={loading} variant="outline" className="gap-2" data-testid="auto-assign-btn">
              <Zap className="w-4 h-4" /> {t('fleet.autoAssign')}
            </Button>
            <Button onClick={() => setChatOpen(!chatOpen)} variant="outline" className="gap-2" data-testid="fleet-chat-btn">
              <MessageCircle className="w-4 h-4" /> FleetBot
            </Button>
            <Button onClick={() => setShowAddDriver(true)} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="add-driver-btn">
              <UserPlus className="w-4 h-4" /> {t('fleet.addDriver')}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: t('fleet.drivers'), value: activeDrivers.length, icon: Users, color: 'text-blue-400' },
            { label: t('fleet.available'), value: availableCount, icon: CircleDot, color: 'text-emerald-400' },
            { label: t('fleet.activeDeliveries'), value: activeDeliveries.length, icon: Package, color: 'text-amber-400' },
            { label: t('fleet.avgTime'), value: `${stats?.avg_delivery_time_min || 0} ${t('fleet.min')}`, icon: Clock, color: 'text-violet-400' },
          ].map((s, i) => (
            <div key={i} className="bg-card border border-border/50 rounded-xl p-4" data-testid={`stat-card-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <span className="text-2xl font-bold">{s.value}</span>
              </div>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit" data-testid="fleet-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Ready Orders Awaiting Assignment */}
            <div className="bg-card border border-border/50 rounded-xl p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-500" />
                {t('fleet.readyOrders')}
                {readyOrders.length > 0 && <span className="bg-amber-500/20 text-amber-500 text-xs px-2 py-0.5 rounded-full">{readyOrders.length}</span>}
              </h3>
              {readyOrders.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">{t('fleet.noOrdersWaiting')}</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {readyOrders.map(order => (
                    <div key={order.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg" data-testid={`ready-order-${order.id}`}>
                      <div>
                        <p className="text-sm font-medium">{order.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{order.total_price} {t('common.sar')} - {order.items?.length || 0} {t('fleet.items')}</p>
                      </div>
                      <Button size="sm" onClick={() => setAssignModal(order)} className="bg-amber-600 hover:bg-amber-700 text-xs" data-testid={`assign-btn-${order.id}`}>
                        {t('fleet.assignDriver')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active Deliveries */}
            <div className="bg-card border border-border/50 rounded-xl p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Truck className="w-4 h-4 text-emerald-500" />
                {t('fleet.activeDeliveriesTitle')}
                {activeDeliveries.length > 0 && <span className="bg-emerald-500/20 text-emerald-500 text-xs px-2 py-0.5 rounded-full">{activeDeliveries.length}</span>}
              </h3>
              {activeDeliveries.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">{t('fleet.noActiveDeliveries')}</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {activeDeliveries.map(d => {
                    const driver = drivers.find(dr => dr.id === d.driver_id);
                    const st = DELIVERY_STATUS[d.status] || {};
                    return (
                      <div key={d.id} className="p-3 bg-muted/30 rounded-lg" data-testid={`active-delivery-${d.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{d.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{driver?.name || '?'} - {d.total_price} {t('common.sar')}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${st.bg} ${st.color}`}>
                            {t(st.key)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Drivers Tab */}
        {tab === 'drivers' && (
          <div className="space-y-3">
            {/* Driver Portal Access Info */}
            <div className="bg-gradient-to-l from-emerald-500/5 to-transparent border border-emerald-500/20 rounded-xl p-4" data-testid="driver-portal-info">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold flex items-center gap-2 mb-1">
                    <ExternalLink className="w-4 h-4 text-emerald-500" />
                    {t('fleet.driverPortal')}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    {t('fleet.portalInfo')}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs bg-muted px-2 py-1 rounded font-mono" data-testid="branch-id-display">{branchId}</code>
                    <button onClick={() => { navigator.clipboard.writeText(branchId); setCopied('branch'); setTimeout(() => setCopied(''), 2000); }}
                      className="text-xs text-emerald-500 hover:text-emerald-400 flex items-center gap-1" data-testid="copy-branch-id">
                      {copied === 'branch' ? <><ClipboardCheck className="w-3 h-3" /> {t('fleet.copied')}</> : <><Copy className="w-3 h-3" /> {t('fleet.copyBranchId')}</>}
                    </button>
                  </div>
                </div>
                <Button size="sm" onClick={() => window.open(`/driver?branch=${branchId}`, '_blank')}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 flex-shrink-0" data-testid="open-driver-portal">
                  <ExternalLink className="w-3.5 h-3.5" /> {t('fleet.openPortal')}
                </Button>
              </div>
            </div>

            {activeDrivers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{t('fleet.noDriversYet')}</p>
              </div>
            ) : (
              activeDrivers.map(driver => {
                const st = STATUS_CONFIG[driver.status] || STATUS_CONFIG.offline;
                const VehicleIcon = VEHICLE_ICON[driver.vehicle_type] || Car;
                return (
                  <div key={driver.id} className="bg-card border border-border/50 rounded-xl p-4 flex items-center gap-4" data-testid={`driver-card-${driver.id}`}>
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                        <VehicleIcon className="w-5 h-5 text-amber-500" />
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-card ${st.dot}`} />
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">{driver.name}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.color} text-white`}>
                          {t(st.key)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <button onClick={() => { navigator.clipboard.writeText(driver.phone); setCopied(driver.id); setTimeout(() => setCopied(''), 2000); }}
                          className="flex items-center gap-1 hover:text-foreground transition-colors" data-testid={`copy-phone-${driver.id}`}>
                          {copied === driver.id ? <ClipboardCheck className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                          <Phone className="w-3 h-3" /> {driver.phone}
                        </button>
                        <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" /> {driver.rating}{driver.total_ratings ? ` (${driver.total_ratings})` : ''}</span>
                        <span>{driver.total_deliveries} {t('fleet.deliveries')}</span>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => toggleDriverStatus(driver)} data-testid={`toggle-status-${driver.id}`}>
                        {driver.status === 'available' ? t('fleet.goOffline') : t('fleet.goOnline')}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-500 hover:bg-red-500/10" onClick={() => deleteDriver(driver.id)} data-testid={`delete-driver-${driver.id}`}>
                        {t('fleet.remove')}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Deliveries Tab */}
        {tab === 'deliveries' && (
          <div className="space-y-3">
            {deliveries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{t('fleet.noDeliveriesYet')}</p>
              </div>
            ) : (
              deliveries.map(d => {
                const driver = drivers.find(dr => dr.id === d.driver_id);
                const st = DELIVERY_STATUS[d.status] || {};
                return (
                  <div key={d.id} className="bg-card border border-border/50 rounded-xl p-4" data-testid={`delivery-row-${d.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${d.status === 'delivered' ? 'bg-emerald-400' : d.status === 'cancelled' ? 'bg-red-400' : 'bg-amber-400'}`} />
                        <div>
                          <p className="text-sm font-medium">{d.customer_name} <span className="text-muted-foreground">#{d.id?.slice(0, 6)}</span></p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {driver?.name || '?'} - {d.total_price} {t('common.sar')} - {d.items_count} {t('fleet.items')}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${st.bg} ${st.color}`}>
                        {t(st.key)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Ratings Tab */}
        {tab === 'ratings' && (
          <RatingsAnalytics data={ratingsData} t={t} />
        )}

        {/* Add Driver Modal */}
        {showAddDriver && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowAddDriver(false)}>
            <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="add-driver-modal">
              <h3 className="text-lg font-bold mb-4">{t('fleet.addNewDriver')}</h3>
              <div className="space-y-3">
                <Input placeholder={t('fleet.driverName')} value={newDriver.name}
                  onChange={e => setNewDriver(p => ({ ...p, name: e.target.value }))} data-testid="driver-name-input" />
                <Input placeholder={t('fleet.phoneNumber')} value={newDriver.phone}
                  onChange={e => setNewDriver(p => ({ ...p, phone: e.target.value }))} data-testid="driver-phone-input" />
                <Select value={newDriver.vehicle_type} onValueChange={v => setNewDriver(p => ({ ...p, vehicle_type: v }))}>
                  <SelectTrigger data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">{t('fleet.car')}</SelectItem>
                    <SelectItem value="motorcycle">{t('fleet.motorcycle')}</SelectItem>
                    <SelectItem value="bicycle">{t('fleet.bicycle')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 mt-5">
                <Button onClick={addDriver} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="confirm-add-driver">{t('common.add')}</Button>
                <Button variant="outline" onClick={() => setShowAddDriver(false)} className="flex-1">{t('common.cancel')}</Button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Order Modal */}
        {assignModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAssignModal(null)}>
            <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="assign-modal">
              <h3 className="text-lg font-bold mb-2">{t('fleet.assignDriver')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{assignModal.customer_name} - {assignModal.total_price} {t('common.sar')}</p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {activeDrivers.filter(d => d.status === 'available').length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('fleet.noAvailableDrivers')}</p>
                ) : (
                  activeDrivers.filter(d => d.status === 'available').map(driver => (
                    <button key={driver.id} onClick={() => assignOrder(assignModal.id, driver.id)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-start"
                      data-testid={`pick-driver-${driver.id}`}>
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <Car className="w-4 h-4 text-emerald-500" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{driver.name}</p>
                        <p className="text-xs text-muted-foreground">{driver.phone} - <Star className="w-3 h-3 inline text-amber-400" /> {driver.rating}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
              <Button variant="outline" onClick={() => setAssignModal(null)} className="w-full mt-3">{t('common.cancel')}</Button>
            </div>
          </div>
        )}

        {/* FleetBot Chat */}
        {chatOpen && (
          <div className="fixed bottom-4 left-4 w-96 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-2xl shadow-2xl z-50 flex flex-col" style={{ height: '450px' }} data-testid="fleetbot-chat">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Truck className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold">FleetBot</p>
                  <p className="text-[10px] text-muted-foreground">{t('fleet.fleetbotAgent')}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setChatOpen(false)} className="h-8 w-8 p-0">
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {chatHistory.length === 0 && (
                <div className="text-center text-muted-foreground text-sm py-8">
                  {t('fleet.askFleetbot')}
                </div>
              )}
              {chatHistory.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-amber-600 text-white' : 'bg-muted'}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {chatLoading && <div className="flex justify-start"><div className="bg-muted rounded-xl px-3 py-2 text-sm animate-pulse">...</div></div>}
            </div>
            <div className="p-3 border-t border-border flex gap-2">
              <Input value={chatMsg} onChange={e => setChatMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendChat()}
                placeholder={t('fleet.typeMessage')} className="text-sm" data-testid="fleetbot-input" />
              <Button onClick={sendChat} disabled={chatLoading} className="bg-amber-600 hover:bg-amber-700" data-testid="fleetbot-send">
                {t('fleet.send')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
}


/* ========== Ratings Analytics Component ========== */
function RatingsAnalytics({ data, t }) {
  if (!data || data.total === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground" data-testid="ratings-empty">
        <Star className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="font-medium">{t('fleet.noRatingsYet')}</p>
        <p className="text-xs mt-1">{t('fleet.ratingsWillAppear')}</p>
      </div>
    );
  }

  const STAR_COLORS = ['#EF4444', '#F97316', '#EAB308', '#84CC16', '#22C55E'];
  const STAR_LABELS = [t('track.star_1'), t('track.star_2'), t('track.star_3'), t('track.star_4'), t('track.star_5')];
  const maxDist = Math.max(...data.stars_distribution, 1);

  return (
    <div className="space-y-4" data-testid="ratings-analytics">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border border-border/50 rounded-xl p-4" data-testid="rating-stat-avg">
          <div className="flex items-center justify-between mb-2">
            <Star className="w-5 h-5 text-amber-400" />
            <span className="text-2xl font-bold">{data.average}</span>
          </div>
          <p className="text-xs text-muted-foreground">{t('fleet.avgRating')}</p>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4" data-testid="rating-stat-total">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            <span className="text-2xl font-bold">{data.total}</span>
          </div>
          <p className="text-xs text-muted-foreground">{t('fleet.totalRatings')}</p>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4" data-testid="rating-stat-positive">
          <div className="flex items-center justify-between mb-2">
            <ThumbsUp className="w-5 h-5 text-emerald-400" />
            <span className="text-2xl font-bold">{data.positive_pct}%</span>
          </div>
          <p className="text-xs text-muted-foreground">{t('fleet.positiveRatings')}</p>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4" data-testid="rating-stat-comments">
          <div className="flex items-center justify-between mb-2">
            <MessageSquare className="w-5 h-5 text-violet-400" />
            <span className="text-2xl font-bold">{data.recent?.length || 0}</span>
          </div>
          <p className="text-xs text-muted-foreground">{t('fleet.ratingsWithComments')}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Stars distribution */}
        <div className="bg-card border border-border/50 rounded-xl p-5" data-testid="stars-distribution">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-amber-500" />
            {t('fleet.starsDistribution')}
          </h3>
          <div className="space-y-3">
            {[5, 4, 3, 2, 1].map(star => {
              const count = data.stars_distribution[star - 1];
              const pct = Math.round((count / maxDist) * 100);
              return (
                <div key={star} className="flex items-center gap-3" data-testid={`dist-star-${star}`}>
                  <div className="flex items-center gap-1 w-12 flex-shrink-0">
                    <span className="text-xs font-bold">{star}</span>
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                  </div>
                  <div className="flex-1 h-3 bg-muted/50 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: STAR_COLORS[star - 1] }} />
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-left">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Driver rankings */}
        <div className="bg-card border border-border/50 rounded-xl p-5" data-testid="driver-rankings">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            {t('fleet.driverRankings')}
          </h3>
          {data.driver_rankings?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{t('fleet.noDriverRatings')}</p>
          ) : (
            <div className="space-y-3">
              {data.driver_rankings?.map((drv, i) => (
                <div key={drv.driver_id} className="flex items-center gap-3" data-testid={`ranking-${i}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-zinc-400/20 text-zinc-300' : i === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-muted text-muted-foreground'
                  }`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{drv.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`w-2.5 h-2.5 ${s <= Math.round(drv.avg_rating) ? 'text-amber-400 fill-amber-400' : 'text-zinc-700'}`} />
                        ))}
                      </div>
                      <span className="text-[10px] text-muted-foreground">{drv.avg_rating} ({drv.total_ratings})</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-20">
                    <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-700"
                        style={{ width: `${(drv.avg_rating / 5) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent comments */}
      {data.recent?.length > 0 && (
        <div className="bg-card border border-border/50 rounded-xl p-5" data-testid="recent-ratings">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-violet-500" />
            {t('fleet.recentComments')}
          </h3>
          <div className="space-y-3">
            {data.recent.map((r, i) => (
              <div key={i} className="flex gap-3 p-3 bg-muted/20 rounded-lg" data-testid={`comment-${i}`}>
                <div className="flex-shrink-0 mt-0.5">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-3 h-3 ${s <= r.stars ? 'text-amber-400 fill-amber-400' : 'text-zinc-700'}`} />
                    ))}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{r.comment}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{r.customer_name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
